import React, { Component } from 'react';
import { motion } from 'framer-motion';
export const Button = ({
  children,
  variant = 'solid',
  href,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  variant?: 'solid' | 'ghost' | 'outline';
  href?: string;
  className?: string;
  onClick?: () => void;
}) => {
  const baseStyle = 'relative inline-flex items-center justify-center px-6 py-3 font-sans font-medium transition-all duration-300 ease-out rounded-sm overflow-hidden group';
  const variants = {
    solid: 'bg-gold text-navy hover:bg-gold-bright shadow-[0_0_15px_rgba(201,162,74,0.2)] hover:shadow-[0_0_25px_rgba(212,118,60,0.4)]',
    ghost: 'bg-transparent text-gold border border-gold/50 hover:border-kesari hover:text-kesari hover:bg-kesari/5',
    outline: 'bg-transparent text-charcoal border border-bronze/30 hover:border-kesari hover:text-kesari hover:bg-kesari/5'
  };
  const Component = href ? 'a' : 'button';
  return <Component href={href} onClick={onClick} className={`${baseStyle} ${variants[variant]} ${className}`} data-id="element-287">
      <span className="relative z-10 flex items-center" data-id="element-288">{children}</span>
      {variant === 'solid' && <div className="absolute inset-0 bg-gradient-to-r from-kesari/0 via-kesari/20 to-kesari/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" data-id="element-289" />}
    </Component>;
};
export const SectionHeader = ({
  title,
  subtitle,
  gurmukhiAccent,
  centered = false,
  dark = false
}: {
  title: string;
  subtitle?: string;
  gurmukhiAccent?: string;
  centered?: boolean;
  dark?: boolean;
}) => <div className={`mb-12 md:mb-20 ${centered ? 'text-center flex flex-col items-center' : ''}`} data-id="element-290">
    {gurmukhiAccent && <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="font-gurmukhi text-kesari text-lg md:text-xl mb-3 tracking-widest opacity-80" data-id="element-291">
        {gurmukhiAccent}
      </motion.div>}
    <motion.h2 initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className={`font-serif text-3xl md:text-5xl font-medium tracking-tight mb-4 text-balance ${dark ? 'text-ivory' : 'text-navy'}`} data-id="element-292">
      {title}
    </motion.h2>
    {subtitle && <motion.p initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.1
  }} className={`font-sans text-lg md:text-xl max-w-2xl ${centered ? 'mx-auto' : ''} ${dark ? 'text-parchment/80' : 'text-charcoal/70'}`} data-id="element-293">
        {subtitle}
      </motion.p>}
  </div>;
export const JaliPattern = ({
  className = ''
}: {
  className?: string;
}) => <svg className={className} width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-294">
    <defs data-id="element-295">
      <pattern id="jali" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-296">
        <path d="M20 0L40 20L20 40L0 20Z" fill="none" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.15" data-id="element-297" />
        <circle cx="20" cy="20" r="14" fill="none" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.15" data-id="element-298" />
        <path d="M0 0L40 40M40 0L0 40" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.1" data-id="element-299" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#jali)" data-id="element-300" />
  </svg>;
export const GeometricMark = ({
  className = ''
}: {
  className?: string;
}) => <motion.svg animate={{
  rotate: 360
}} transition={{
  duration: 120,
  repeat: Infinity,
  ease: 'linear'
}} className={className} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-301">
    <motion.path initial={{
    pathLength: 0,
    opacity: 0
  }} animate={{
    pathLength: 1,
    opacity: 0.3
  }} transition={{
    duration: 2,
    ease: 'easeInOut'
  }} d="M50 5L95 50L50 95L5 50L50 5Z" stroke="currentColor" strokeWidth="1" data-id="element-302" />
    <motion.path initial={{
    pathLength: 0,
    opacity: 0
  }} animate={{
    pathLength: 1,
    opacity: 0.3
  }} transition={{
    duration: 2,
    delay: 0.5,
    ease: 'easeInOut'
  }} d="M25 25L75 25L75 75L25 75L25 25Z" stroke="currentColor" strokeWidth="1" data-id="element-303" />
    <circle cx="50" cy="50" r="35" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" data-id="element-304" />
    <circle cx="50" cy="50" r="20" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" data-id="element-305" />
    <motion.path animate={{
    scale: [1, 1.05, 1],
    opacity: [0.8, 1, 0.8]
  }} transition={{
    duration: 4,
    repeat: Infinity,
    ease: 'easeInOut'
  }} d="M50 20L55 45L80 50L55 55L50 80L45 55L20 50L45 45L50 20Z" fill="currentColor" className="origin-center" data-id="element-306" />
  </motion.svg>;